import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';
import { Row, Col } from 'react-bootstrap';

class TemplateComparison extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sampleData: {
                nom: 'DUPONT',
                prenom: 'Jean',
                numero_carte: '1234567890123456',
                date_expiration: '12/2026'
            }
        };
    }

    renderMiniCardPreview = (template) => {
        if (!template) return null;

        const cardStyle = {
            width: '200px',
            height: '125px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '10px',
            position: 'relative',
            fontSize: '8px',
            fontFamily: 'Arial, sans-serif',
            margin: '0 auto'
        };

        return (
            <div className="text-center mb-3">
                <div style={cardStyle}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>
                        CARTE D'ASSURANCE
                    </div>
                    <div style={{ fontSize: '9px', fontWeight: 'bold', marginTop: '30px' }}>
                        {this.state.sampleData.prenom} {this.state.sampleData.nom}
                    </div>
                    <div style={{ fontSize: '8px', position: 'absolute', bottom: '10px', left: '10px' }}>
                        {this.state.sampleData.numero_carte}
                    </div>
                </div>
            </div>
        );
    };

    renderComparisonTable = () => {
        const { templates } = this.props;
        if (!templates || templates.length === 0) return null;

        const comparisonData = [
            {
                attribute: 'Nom',
                values: templates.map(t => t.name)
            },
            {
                attribute: 'Pays',
                values: templates.map(t => t.country)
            },
            {
                attribute: 'Type',
                values: templates.map(t => t.type)
            },
            {
                attribute: 'Catégorie',
                values: templates.map(t => t.category)
            },
            {
                attribute: 'Version',
                values: templates.map(t => t.version)
            },
            {
                attribute: 'Statut',
                values: templates.map(t => t.isActive ? 'Actif' : 'Inactif')
            },
            {
                attribute: 'Par défaut',
                values: templates.map(t => t.isDefault ? 'Oui' : 'Non')
            },
            {
                attribute: 'Utilisations',
                values: templates.map(t => t.usageCount.toString())
            },
            {
                attribute: 'Variables',
                values: templates.map(t => t.variables ? t.variables.length.toString() : '0')
            },
            {
                attribute: 'Créé le',
                values: templates.map(t => new Date(t.createdAt).toLocaleDateString())
            },
            {
                attribute: 'Modifié le',
                values: templates.map(t => new Date(t.updatedAt).toLocaleDateString())
            }
        ];

        const attributeBodyTemplate = (rowData) => {
            return <strong>{rowData.attribute}</strong>;
        };

        const valueBodyTemplate = (columnIndex) => (rowData) => {
            const value = rowData.values[columnIndex];

            // Formatage spécial pour certains attributs
            if (rowData.attribute === 'Statut') {
                return (
                    <Badge
                        value={value}
                        severity={value === 'Actif' ? 'success' : 'danger'}
                    />
                );
            }
            if (rowData.attribute === 'Par défaut') {
                return (
                    <Badge
                        value={value}
                        severity={value === 'Oui' ? 'info' : 'secondary'}
                    />
                );
            }
            if (rowData.attribute === 'Utilisations') {
                return <span className="font-bold text-primary">{value}</span>;
            }

            return value;
        };

        return (
            <DataTable
                value={comparisonData}
                responsiveLayout="scroll"
                className="comparison-table"
            >
                <Column
                    field="attribute"
                    header="Attribut"
                    body={attributeBodyTemplate}
                    style={{ width: '200px', fontWeight: 'bold' }}
                />
                {templates.map((template, index) => (
                    <Column
                        key={template.id}
                        header={template.name}
                        body={valueBodyTemplate(index)}
                        style={{ minWidth: '200px' }}
                    />
                ))}
            </DataTable>
        );
    };

    renderVariablesComparison = () => {
        const { templates } = this.props;
        if (!templates || templates.length === 0) return null;

        // Récupérer toutes les variables uniques
        const allVariables = new Set();
        templates.forEach(template => {
            if (template.variables) {
                template.variables.forEach(variable => allVariables.add(variable));
            }
        });

        const variablesArray = Array.from(allVariables);

        return (
            <Card title="Comparaison des Variables" className="mt-4">
                <DataTable
                    value={variablesArray.map(variable => ({ variable }))}
                    responsiveLayout="scroll"
                >
                    <Column
                        field="variable"
                        header="Variable"
                        body={(rowData) => <strong>{rowData.variable}</strong>}
                        style={{ width: '200px' }}
                    />
                    {templates.map((template, index) => (
                        <Column
                            key={template.id}
                            header={template.name}
                            body={(rowData) => {
                                const hasVariable = template.variables &&
                                    template.variables.includes(rowData.variable);
                                return hasVariable ?
                                    <i className="pi pi-check text-green-500"></i> :
                                    <i className="pi pi-times text-red-500"></i>;
                            }}
                            style={{ textAlign: 'center', minWidth: '200px' }}
                        />
                    ))}
                </DataTable>
            </Card>
        );
    };

    renderRecommendations = () => {
        const { templates } = this.props;
        if (!templates || templates.length < 2) return null;

        const recommendations = [];

        // Analyser les différences et générer des recommandations
        const countries = [...new Set(templates.map(t => t.country))];
        const types = [...new Set(templates.map(t => t.type))];
        const categories = [...new Set(templates.map(t => t.category))];

        if (countries.length > 1) {
            recommendations.push({
                type: 'info',
                icon: 'pi pi-info-circle',
                title: 'Différents pays',
                message: `Ces templates sont conçus pour différents pays : ${countries.join(', ')}`
            });
        }

        if (types.length > 1) {
            recommendations.push({
                type: 'warning',
                icon: 'pi pi-exclamation-triangle',
                title: 'Types différents',
                message: `Attention aux différences de types : ${types.join(', ')}`
            });
        }

        // Vérifier les variables communes
        const commonVariables = templates.reduce((common, template) => {
            if (!template.variables) return common;
            return common.filter(variable => template.variables.includes(variable));
        }, templates[0]?.variables || []);

        if (commonVariables.length > 0) {
            recommendations.push({
                type: 'success',
                icon: 'pi pi-check',
                title: 'Variables communes',
                message: `Variables partagées : ${commonVariables.join(', ')}`
            });
        }

        // Vérifier les templates par défaut
        const defaultTemplates = templates.filter(t => t.isDefault);
        if (defaultTemplates.length > 1) {
            recommendations.push({
                type: 'error',
                icon: 'pi pi-times',
                title: 'Conflit templates par défaut',
                message: 'Plusieurs templates sont marqués comme défaut pour le même contexte'
            });
        }

        return (
            <Card title="Recommandations" className="mt-4">
                {recommendations.length === 0 ? (
                    <p className="text-muted">Aucune recommandation particulière.</p>
                ) : (
                    <div className="recommendations-list">
                        {recommendations.map((rec, index) => (
                            <div key={index} className={`recommendation-item p-3 mb-2 border-round border-left-3 border-${rec.type}`}>
                                <div className="flex align-items-start gap-3">
                                    <i className={`${rec.icon} text-${rec.type} text-xl`}></i>
                                    <div>
                                        <h6 className="m-0 mb-1">{rec.title}</h6>
                                        <p className="m-0 text-sm">{rec.message}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        );
    };

    render() {
        const { visible, onHide, templates } = this.props;

        if (!templates || templates.length === 0) {
            return (
                <Dialog
                    visible={visible}
                    style={{ width: '600px' }}
                    header="Comparaison de Templates"
                    modal
                    onHide={onHide}
                >
                    <div className="text-center p-4">
                        <i className="pi pi-info-circle text-6xl text-muted mb-3"></i>
                        <h5>Aucun template sélectionné</h5>
                        <p className="text-muted">
                            Veuillez sélectionner au moins deux templates pour les comparer.
                        </p>
                    </div>
                </Dialog>
            );
        }

        return (
            <Dialog
                visible={visible}
                style={{ width: '95vw', height: '90vh' }}
                header={`Comparaison de Templates (${templates.length})`}
                modal
                onHide={onHide}
                maximizable
                footer={
                    <div>
                        <Button
                            label="Fermer"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={onHide}
                        />
                        <Button
                            label="Exporter Comparaison"
                            icon="pi pi-download"
                            onClick={() => {
                                // Logique d'export de la comparaison
                                console.log('Export comparaison...');
                            }}
                        />
                    </div>
                }
            >
                <div className="template-comparison-container">
                    {/* Aperçu des cartes */}
                    <Card title="Aperçu des Templates" className="mb-4">
                        <Row>
                            {templates.map((template, index) => (
                                <Col key={template.id} md={12 / Math.min(templates.length, 4)} className="mb-3">
                                    <div className="text-center">
                                        <h6 className="mb-2">{template.name}</h6>
                                        {this.renderMiniCardPreview(template)}
                                        <div className="template-summary">
                                            <small className="text-muted">
                                                {template.country} - {template.type}<br/>
                                                {template.usageCount} utilisations
                                            </small>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>

                    <Divider />

                    {/* Tableau de comparaison */}
                    <Card title="Comparaison Détaillée" className="mb-4">
                        {this.renderComparisonTable()}
                    </Card>

                    {/* Comparaison des variables */}
                    {this.renderVariablesComparison()}

                    {/* Recommandations */}
                    {this.renderRecommendations()}
                </div>
            </Dialog>
        );
    }
}

export default TemplateComparison;